import { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PopOverForTimeValidation } from '../common/PopOverForTimeValidation';
import { blockInvalidChar, inputValidation, inputValidationOnBlur } from '../../utils/validations';
import type { FreezeDateData, SelectedTaskValue, TimeEntryCell } from '../../types';

/**
 * Port of `timeEntryRightPanelComponents/selectedTask.jsx`.
 *
 * Dropped: the `inputValidationForRemainingHours` import (the export is commented out in
 * validations.js, so it has always been `undefined`), the unused `ProgressBar` import, and
 * an empty `componentDidUpdate` whose only branch had no body.
 *
 * The "Remaining Hours" input is purely local — its value is never sent anywhere and never
 * read by a parent. That is the original's behaviour, not an omission.
 */

/**
 * `a <= b` with the original's untyped operands.
 *
 * `date` and `freezeDateData.configValue` are both `string | undefined` and are compared
 * with `<=` in two places. JavaScript compares them as strings, and as `undefined`
 * whenever either is missing (which yields false). TypeScript rejects the comparison
 * outright, so the coercion is isolated here rather than scattered as casts.
 */
function lte(a: unknown, b: unknown): boolean {
  return (a as string) <= (b as string);
}

interface SelectedTaskProps {
  selectedTaskValue: SelectedTaskValue;
  userTimeSheetData: unknown;
  freezeDateData: FreezeDateData | undefined;
  validateTimeEntered_TimeType: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  addTimeEntered_TimeType: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  highlightSelectedInputOfTimeType: (
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  enterInvoiceCommentOfTimeTypeProject: (
    value: string,
    selectedTaskValue: SelectedTaskValue,
    entry: TimeEntryCell,
  ) => void;
  /* Passed by timeEntryRightPanel.jsx but never read here — kept so the parent type-checks.
     `isDisabled`/`clickInput` are passed from the panel's own (always empty) state, so they
     are permanently `true`/`undefined`. */
  updateSelectedTaskValue?: (selectedTaskValue: SelectedTaskValue) => void;
  isDisabled?: boolean;
  clickInput?: boolean;
}

export function SelectedTask(props: SelectedTaskProps) {
  const [inputValue, setInputValue] = useState<string | number>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const numbersOnly = event.target.value.replace(/[^0-9.]/g, '');
    setInputValue(inputValidation(numbersOnly, 5));
  };

  const remainingHoursInputValidationOnBlure = (): void => {
    setInputValue(inputValidationOnBlur(inputValue));
  };

  const renderInputComponent = (entry: TimeEntryCell, date: string | undefined) => (
    <input
      tabIndex={0}
      data-toggle="tooltip"
      data-placement="left"
      // Both carry `entry.time`, which is a number whenever the BFF supplies one — wider
      // than the string these attributes are typed to accept, and rendered as-is today.
      {...({ title: entry?.time, alt: entry?.time } as React.InputHTMLAttributes<HTMLInputElement>)}
      className={`timeSlot ${
        props.selectedTaskValue?.timedate?.istimeTypeEnableProject
          ? entry?.timeEntryPendingStatus === 'A' || entry?.timeEntryPendingStatus === 'R'
            ? 'PS'
            : entry?.timeEntryStatus
          : ''
      } ${
        props.selectedTaskValue?.timedate?.isFreezed === 'Y'
          ? lte(date, props.freezeDateData?.configValue) && props.freezeDateData?.isFreezed
            ? 'quater_freeze'
            : 'week_freeze'
          : ''
      }`}
      name="time"
      ref={(inputElement) => {
        if (inputElement) {
          if (entry?.timeError) {
            inputElement.select();
            inputElement.focus();
          }
          if (!entry?.timeError && entry?.isSelected) {
            inputElement.select();
          }
          if (entry?.focused) {
            inputElement.focus();
          }
        }
      }}
      disabled={
        entry?.timeEntryStatus === 'B' ||
        entry?.timeEntryStatus === 'A' ||
        entry?.isFreezed === 'Y'
      }
      value={entry?.time}
      onKeyUp={(e) => {
        if (blockInvalidChar.includes(e.key)) {
          (e.target as HTMLInputElement).value = '';
        }
      }}
      onBlur={(event) =>
        props.validateTimeEntered_TimeType(event.target.value, props.selectedTaskValue, entry)
      }
      onChange={(event) =>
        props.addTimeEntered_TimeType(event.target.value, props.selectedTaskValue, entry)
      }
      onClick={() => {
        props.highlightSelectedInputOfTimeType(props.selectedTaskValue, entry);
      }}
      onCopy={(e) => {
        e.preventDefault();
        return false;
      }}
      onPaste={(e) => {
        e.preventDefault();
        return false;
      }}
    />
  );

  const renderStatusForTooltip = (entry: TimeEntryCell, date: string | undefined) => (
    <>
      {entry?.isFreezed === 'Y' && (
        <>
          <strong>Time Restriction:</strong>{' '}
          <span>
            {entry?.isFreezed === 'Y' &&
            lte(date, props.freezeDateData?.configValue) &&
            props.freezeDateData?.isFreezed
              ? entry?.isFreezedMessage !== null
                ? entry?.isFreezedMessage
                : ' Quarter Time Frozen'
              : entry?.isFreezedMessage !== null
                ? entry?.isFreezedMessage
                : ' Weekly Time Frozen'}
          </span>
          <br></br>
        </>
      )}
      {!(entry?.timeEntryStatus === 'S' && entry?.time === 0) && (
        <>
          <strong>Time Status: </strong>{' '}
          <span>
            {entry?.timeEntryPendingStatus === 'A' || entry?.timeEntryPendingStatus === 'R'
              ? 'Time Under Review for Approval'
              : entry?.timeEntryStatus === 'U'
                ? 'Time Submitted for Approval'
                : entry?.timeEntryStatus === 'A'
                  ? 'Time Approved by Time Approver'
                  : entry?.timeEntryStatus === 'R'
                    ? 'Time Rejected by Time Approver'
                    : entry?.timeEntryStatus === 'B'
                      ? 'Cell Blocked due to the end of the Project/Allocation/Task or Task Assignment'
                      : entry?.timeEntryStatus === 'S' && 'Time Saved'}
          </span>
        </>
      )}
    </>
  );

  /** Recolours the Bootstrap tooltip's inner body as it enters. */
  const entering = (e: HTMLElement): void => {
    const body = e.children[1] as HTMLElement;
    body.style.backgroundColor = '#2b7bcc';
    body.style.color = '#fff';
    body.style.borderColor = '#2b7bcc';
  };

  const estPercentage = props.selectedTaskValue.task?.estTimePersentage
    ? props.selectedTaskValue.task?.estTimePersentage
    : 0;

  return (
    <div
      style={{
        height: '62vh',
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
    >
      <div className="ch-selected">
        <div
          className="ch-selected-task-name"
          tabIndex={0}
          title={`${props.selectedTaskValue?.task?.taskName}`}
        >
          {props.selectedTaskValue?.task?.taskName}
        </div>
        <div>
          <div className="row ch-selected-info">
            <div
              className="ch-50col selected-lbl"
              tabIndex={0}
              {...{
                alt: `Plan start date is ${props.selectedTaskValue?.task?.taskAssignmentStartDate}`,
              }}
            >
              <div className="">
                <label>Plan Start</label>
              </div>
              <div className="selected-data">
                <span>{props.selectedTaskValue?.task?.taskAssignmentStartDate}</span>
              </div>
            </div>
            <div
              className="ch-50col selected-lbl"
              tabIndex={0}
              {...{
                alt: `Plan End date is ${props.selectedTaskValue.task?.taskAssignmentEndDate}`,
              }}
            >
              <div className="">
                <label>Plan End</label>
              </div>
              <div className="selected-data">
                <span>{props.selectedTaskValue.task?.taskAssignmentEndDate}</span>
              </div>
            </div>
          </div>

          <svg
            tabIndex={0}
            {...{ alt: `${estPercentage}%` }}
            id="prgresBar"
            width="100%"
            height="16"
            style={{ display: 'block', backgroundColor: 'transparent' }}
          >
            <rect x="0" width="100%" height="16" fill="#cacaca" style={{ strokeWidth: '-1' }}></rect>
            <rect
              x="0"
              y="0"
              height="16"
              fill="#e84c3d"
              width={`${estPercentage}%`}
              style={{ strokeWidth: '0' }}
            ></rect>
            <g
              fill="#222"
              textAnchor="middle"
              fontFamily="DejaVu Sans,Verdana,Geneva,sans-serif"
              fontSize="11px"
            >
              <text x="47%" y="11.666666666666666" fill="#222">
                {estPercentage}%
              </text>
            </g>
          </svg>

          <div className="row ch-selected-info ">
            {/* The stray "$  {" inside this alt string is in the original — it renders
                literally rather than interpolating loggedHours. Kept verbatim. */}
            <text
              className=""
              tabIndex={0}
              {...{
                alt: `Estimated Hours: ${props.selectedTaskValue.task?.estimatedHours} and Logged Hours: $  {this.props.selectedTaskValue.task?.loggedHours} `,
              }}
            >
              <span className="logged-hour-label-left">
                {' '}
                {'Est (hrs) : '} {props.selectedTaskValue.task?.estimatedHours}
              </span>
              <span className="logged-hour-label-center"> {' | '}</span>
              <span className="logged-hour-label-right">
                {'Logged (hrs) : '}

                {props.selectedTaskValue.task?.loggedHours}
              </span>
            </text>
          </div>

          <div className="row ch-selected-info">
            <div className="ch-rem-hrs">
              <span {...{ alt: 'Remaining Hours' }}>Remaining Hours :</span>
              <input
                tabIndex={0}
                data-toggle="tooltip"
                data-placement="left"
                title={`Remaining Hours ${inputValue}`}
                {...{ alt: `Remaining Hours ${inputValue}` }}
                value={inputValue}
                onChange={handleChange}
                onBlur={() => {
                  remainingHoursInputValidationOnBlure();
                }}
              />
            </div>
          </div>
          <div className="ch-selected-info">
            <div className="">
              {props.selectedTaskValue?.timedate?.istimeTypeEnableProject &&
                props.selectedTaskValue?.timedate?.timeEntry?.map((entry, index) => (
                  <div className="selected-info-more" key={index}>
                    <div className="row input-lbl">{entry?.timeType}</div>
                    {entry?.timeError && <PopOverForTimeValidation />}
                    <div className="row input-tag">
                      {/* An edited cell renders a bare input; an untouched one is wrapped
                          in the status tooltip. */}
                      {(entry?.isChanged &&
                        entry?.initialTime !==
                          (entry?.time === '' ? 0 : parseFloat(entry?.time as string))) ||
                      (entry.timeEntryStatus === 'S' &&
                        entry?.time === 0 &&
                        !(entry.isFreezed === 'Y')) ? (
                        renderInputComponent(entry, props.selectedTaskValue?.timedate?.date)
                      ) : (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              id={`tooltip-${entry?.timeEntryId}`}
                              className="time_status_tooltip"
                              {...{ trigger: ['hover', 'focus'] }}
                            >
                              {renderStatusForTooltip(
                                entry,
                                props.selectedTaskValue?.timedate?.date,
                              )}
                            </Tooltip>
                          }
                          onEntering={(e) => {
                            entering(e);
                          }}
                        >
                          {renderInputComponent(entry, props.selectedTaskValue?.timedate?.date)}
                        </OverlayTrigger>
                      )}
                      <input
                        tabIndex={0}
                        data-toggle="tooltip"
                        data-placement="left"
                        {...{
                          alt:
                            entry?.invoiceComment === null || entry?.invoiceComment === 'null'
                              ? ''
                              : entry?.invoiceComment,
                        }}
                        title={
                          entry?.invoiceComment === null || entry?.invoiceComment === 'null'
                            ? ''
                            : (entry?.invoiceComment as string)
                        }
                        type="text"
                        name="entry-text"
                        maxLength={256}
                        value={
                          entry?.invoiceComment === null || entry?.invoiceComment === 'null'
                            ? ''
                            : (entry?.invoiceComment as string)
                        }
                        onBlur={(event) =>
                          props.enterInvoiceCommentOfTimeTypeProject(
                            event.target.value,
                            props.selectedTaskValue,
                            entry,
                          )
                        }
                        onChange={(event) =>
                          props.enterInvoiceCommentOfTimeTypeProject(
                            event.target.value,
                            props.selectedTaskValue,
                            entry,
                          )
                        }
                        onClick={(event) =>
                          props.enterInvoiceCommentOfTimeTypeProject(
                            (event.target as HTMLInputElement).value,
                            props.selectedTaskValue,
                            entry,
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
